import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Search, Compass, ChevronRight, X, MapPin, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useI18n } from '@/lib/i18n';
import { useCountry } from '@/lib/countryContext';
import { useSelectedCity } from '@/lib/selectedCity';

/**
 * Full-page city picker. Replaces the older bottom-sheet — a real page
 * sidesteps every mobile-keyboard headache because the OS treats the
 * document as a normal scrollable view and never covers the focused input.
 *
 * Route: `/select-city?returnTo=/some/path`
 * On pick → persists the selection and `navigate(returnTo, { replace: true })`.
 */
export default function CitySelectPage() {
  const { language } = useI18n();
  const { countryCode } = useCountry();
  const [selectedCity, setSelectedCity] = useSelectedCity();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo') || '/';

  const [query, setQuery] = useState('');
  const [popularCities, setPopularCities] = useState([]);
  const [results, setResults] = useState([]);
  const [loadingPopular, setLoadingPopular] = useState(true);
  const [searching, setSearching] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const inputRef = useRef(null);

  // Load popular cities (scoped to the current country) so the user sees a
  // curated list even before typing anything.
  useEffect(() => {
    let mounted = true;
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${baseUrl}/api/cities?country_code=${countryCode || 'MX'}&with_businesses=true`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => { if (mounted && Array.isArray(data)) setPopularCities(data); })
      .catch(() => setPopularCities([]))
      .finally(() => { if (mounted) setLoadingPopular(false); });
    return () => { mounted = false; };
  }, [countryCode]);

  // Debounced global search — kicks in when the local (popular) list can't
  // match. Uses country_code=ALL so people traveling can search any city.
  useEffect(() => {
    if (!query || query.trim().length < 2) { setResults([]); return; }
    const localHits = popularCities.filter((c) =>
      (typeof c === 'string' ? c : c.name || '').toLowerCase().includes(query.toLowerCase())
    );
    if (localHits.length > 0) { setResults(localHits); return; }

    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    const controller = new AbortController();
    const t = setTimeout(() => {
      setSearching(true);
      fetch(`${baseUrl}/api/cities?country_code=ALL&q=${encodeURIComponent(query)}`, { signal: controller.signal })
        .then((r) => (r.ok ? r.json() : []))
        .then((data) => {
          if (!Array.isArray(data)) return;
          // The upstream global city search occasionally returns
          // duplicates (same name + state pair). Dedupe by "name|state" so
          // the user never sees "Monterrey, Nuevo León" twice in a row.
          const seen = new Set();
          const unique = data.filter((c) => {
            const key = `${(c?.name || '').toLowerCase()}|${(c?.state || '').toLowerCase()}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          setResults(unique.slice(0, 30));
        })
        .catch(() => {})
        .finally(() => setSearching(false));
    }, 220);
    return () => { clearTimeout(t); controller.abort(); };
  }, [query, popularCities]);

  const handlePick = useCallback((name) => {
    if (!name) return;
    setSelectedCity(name);
    // Small delay so the tap animation registers before we navigate away.
    setTimeout(() => navigate(returnTo, { replace: true }), 120);
  }, [navigate, returnTo, setSelectedCity]);

  const handleUseLocation = useCallback(() => {
    if (!navigator.geolocation) {
      toast.error(language === 'es' ? 'Tu dispositivo no soporta ubicación' : "Your device doesn't support location");
      return;
    }
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const r = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&accept-language=es`,
            { headers: { 'User-Agent': 'Bookvia/1.0' } }
          );
          const d = await r.json();
          const detected = d?.address?.city || d?.address?.town || d?.address?.village || d?.address?.state;
          if (detected) {
            toast.success(language === 'es' ? `Ciudad detectada: ${detected}` : `Detected: ${detected}`);
            handlePick(detected);
          } else {
            toast.error(language === 'es' ? 'No pudimos identificar tu ciudad' : "Couldn't identify your city");
          }
        } catch {
          toast.error(language === 'es' ? 'No pudimos identificar tu ciudad' : "Couldn't identify your city");
        }
        setDetecting(false);
      },
      (err) => {
        setDetecting(false);
        if (err?.code === 1) {
          toast.error(language === 'es'
            ? 'Permiso denegado. Actívalo en Ajustes → Bookvia → Ubicación'
            : 'Permission denied. Enable it in Settings → Bookvia → Location');
        } else if (err?.code === 3) {
          toast.error(language === 'es' ? 'Se agotó el tiempo de ubicación' : 'Location timed out');
        } else {
          toast.error(language === 'es' ? 'No pudimos obtener tu ubicación' : "Couldn't get your location");
        }
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60 * 1000 }
    );
  }, [handlePick, language]);

  const listToShow = useMemo(() => (query.trim().length >= 2 ? results : popularCities), [query, results, popularCities]);
  const showingSearch = query.trim().length >= 2;

  return (
    <div className="min-h-screen bg-[#FAF7F2] dark:bg-[#161311] text-[#2A2A2A] dark:text-[#F5F2ED] flex flex-col">
      {/* Sticky header — back arrow + title. Uses safe-area padding-top so it
          doesn't clip the notch on notched iPhones. */}
      <div
        className="sticky top-0 z-20 bg-[#FAF7F2] dark:bg-[#161311] border-b border-[#EAE3D9]/70 dark:border-[#332D29]"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="flex items-center gap-3 px-4 py-3">
          <button
            onClick={() => navigate(returnTo, { replace: true })}
            className="h-10 w-10 rounded-full grid place-items-center active:bg-[#EAE3D9] dark:active:bg-[#2A2522] transition-colors"
            data-testid="city-select-back"
            aria-label={language === 'es' ? 'Regresar' : 'Back'}
          >
            <ArrowLeft className="h-5 w-5" strokeWidth={2.2} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="font-display font-bold text-[22px] leading-tight">
              {language === 'es' ? 'Elige tu ciudad' : 'Choose your city'}
            </h1>
            <p className="text-[12.5px] text-[#737373] dark:text-[#A39F99] mt-0.5">
              {language === 'es' ? 'Verás los negocios disponibles ahí' : "You'll see businesses available there"}
            </p>
          </div>
        </div>
      </div>

      {/* Search input + "Use location" — sticky under the header so they
          stay reachable while the list scrolls. */}
      <div className="px-4 pt-4 pb-2 shrink-0">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-[18px] w-[18px] text-[#A3A3A3] pointer-events-none" strokeWidth={2.2} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={language === 'es' ? 'Buscar ciudad' : 'Search city'}
            className="w-full pl-11 pr-11 py-3.5 text-[15px] rounded-2xl bg-white dark:bg-[#1E1A17] border border-[#EAE3D9] dark:border-[#332D29] focus:outline-none focus:border-[#E85A4F]/60 focus:ring-2 focus:ring-[#E85A4F]/15 placeholder:text-[#A3A3A3]"
            autoComplete="off"
            inputMode="search"
            data-testid="city-select-search"
          />
          {query && (
            <button
              onClick={() => { setQuery(''); inputRef.current?.focus(); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full bg-[#F3EFE9] dark:bg-[#2A2522] grid place-items-center active:scale-90 transition"
              data-testid="city-select-clear"
              aria-label={language === 'es' ? 'Limpiar' : 'Clear'}
            >
              <X className="h-3.5 w-3.5 text-[#737373]" strokeWidth={2.4} />
            </button>
          )}
        </div>

        <button
          onClick={handleUseLocation}
          disabled={detecting}
          className="w-full mt-3 flex items-center gap-3 px-4 py-3.5 rounded-2xl border border-[#E85A4F]/30 bg-white dark:bg-[#1E1A17] active:bg-[#E85A4F]/8 disabled:opacity-70 transition"
          data-testid="city-select-use-location"
        >
          <div className="h-9 w-9 rounded-xl bg-[#E85A4F]/12 grid place-items-center shrink-0">
            <Compass className="h-[18px] w-[18px] text-[#E85A4F]" strokeWidth={2.4} />
          </div>
          <span className="font-semibold text-[15px] text-[#E85A4F]">
            {detecting
              ? (language === 'es' ? 'Ubicando…' : 'Locating…')
              : (language === 'es' ? 'Usar mi ubicación actual' : 'Use my current location')}
          </span>
        </button>
      </div>

      {/* Section label */}
      <div className="px-4 pt-3 pb-1 flex items-center justify-between shrink-0">
        <p className="text-[13px] font-semibold text-[#737373] dark:text-[#A39F99]">
          {showingSearch
            ? (language === 'es' ? 'Resultados' : 'Results')
            : (language === 'es' ? 'Ciudades disponibles' : 'Available cities')}
        </p>
        {showingSearch && searching && (
          <p className="text-[12px] text-[#A3A3A3]">
            {language === 'es' ? 'Buscando…' : 'Searching…'}
          </p>
        )}
      </div>

      {/* Scrollable list. `flex-1` + normal document scroll → the mobile
          keyboard handles this naturally by scrolling the results into view.
          No visualViewport gymnastics needed. */}
      <div
        className="flex-1 px-3 pb-24 overflow-y-auto"
        style={{ WebkitOverflowScrolling: 'touch' }}
        data-testid="city-select-list"
      >
        {loadingPopular && !showingSearch ? (
          <div className="space-y-2 mt-2 px-1">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-14 rounded-xl bg-white/60 dark:bg-white/5 animate-pulse" />
            ))}
          </div>
        ) : listToShow.length ? (
          <div className="space-y-1">
            {listToShow.map((c) => {
              const name = typeof c === 'string' ? c : (c.name || '');
              const state = typeof c === 'string' ? '' : (c.state || '');
              const count = typeof c === 'string' ? 0 : (c.business_count || 0);
              const active = selectedCity && name.toLowerCase() === selectedCity.toLowerCase();
              return (
                <motion.button
                  whileTap={{ scale: 0.985 }}
                  key={`${name}-${state}`}
                  onClick={() => handlePick(name)}
                  className={`w-full text-left px-3 py-3.5 rounded-xl flex items-center gap-3 transition-colors ${active ? 'bg-[#E85A4F]/8 border border-[#E85A4F]/20' : 'bg-white dark:bg-[#1E1A17] border border-transparent hover:border-[#EAE3D9] dark:hover:border-[#332D29] active:bg-[#F3EFE9] dark:active:bg-[#2A2522]'}`}
                  data-testid={`city-select-option-${name}`}
                >
                  <div className={`h-10 w-10 rounded-xl grid place-items-center shrink-0 ${active ? 'bg-[#E85A4F] text-white' : 'bg-[#F3EFE9] dark:bg-[#2A2522] text-[#737373]'}`}>
                    <MapPin className="h-[18px] w-[18px]" strokeWidth={2.2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[15.5px] font-semibold truncate">
                      {name}{state ? `, ${state}` : ''}
                    </div>
                    {count > 0 && !active && (
                      <div className="text-[12px] text-[#A39F99] mt-0.5">
                        {count} {language === 'es' ? (count === 1 ? 'negocio' : 'negocios') : (count === 1 ? 'business' : 'businesses')}
                      </div>
                    )}
                  </div>
                  {active
                    ? <Check className="h-5 w-5 text-[#E85A4F] shrink-0" strokeWidth={2.6} />
                    : <ChevronRight className="h-5 w-5 text-[#C3BDB4] shrink-0" strokeWidth={2} />}
                </motion.button>
              );
            })}
          </div>
        ) : (
          <div className="px-4 py-10 text-center">
            <div className="mx-auto h-14 w-14 rounded-full bg-[#F3EFE9] dark:bg-[#2A2522] grid place-items-center mb-3">
              <Search className="h-6 w-6 text-[#A3A3A3]" strokeWidth={2} />
            </div>
            <p className="text-[15px] font-semibold">
              {showingSearch
                ? (language === 'es' ? `No encontramos "${query}"` : `No results for "${query}"`)
                : (language === 'es' ? 'Aún no hay ciudades disponibles' : 'No cities available yet')}
            </p>
            <p className="text-[13px] text-[#737373] dark:text-[#A39F99] mt-1.5">
              {showingSearch
                ? (language === 'es' ? 'Prueba con otra ciudad o usa tu ubicación' : 'Try another city or use your location')
                : (language === 'es' ? 'Usa tu ubicación actual para empezar' : 'Use your current location to start')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
